import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import AssetTable from '../../components/assets/AssetTable';
import AssetFilters from '../../components/assets/AssetFilters';
import Loader from '../../components/common/Loader';
import Pagination from '../../components/common/Pagination';
import ConfirmActionModal from '../../components/modals/ConfirmActionModal';
import { exportToExcel, exportToPDF, preparePrintData } from '../../utils/exportUtils';
import { PERMISSIONS, canFeatureForRole } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';
import '../../styles/Table.css';

const AssetListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEmployeeAssetsArea = location.pathname.startsWith('/employee');
  const assetsBasePath = isEmployeeAssetsArea ? '/employee/assets' : '/admin/assets';
  const addAssetPath = `${assetsBasePath}/add`;

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}');
    } catch (error) {
      return {};
    }
  }, []);
  const normalizedUserRole = String(currentUser?.role || '').trim().toLowerCase();
  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();

  const canUseFeature = useCallback(
    (possibleKeys = []) => canFeatureForRole(normalizedUserRole, rolePermissions, possibleKeys),
    [rolePermissions, normalizedUserRole]
  );

  const canViewAssetsList = useMemo(
    () =>
      canFeatureForRole(normalizedUserRole, rolePermissions, [
        PERMISSIONS.VIEW_ALL_ASSETS,
        PERMISSIONS.VIEW_ASSIGNED_ASSETS,
      ]),
    [normalizedUserRole, rolePermissions]
  );

  const canViewAllAssets = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.VIEW_ALL_ASSETS]),
    [normalizedUserRole, rolePermissions]
  );
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [totalAssets, setTotalAssets] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Use refs to prevent re-renders on input changes
  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showUpdateConditionModal, setShowUpdateConditionModal] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());
  
  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);
  const [sortBy, setSortBy] = useState('assetTag'); // Default sort by Tag ID
  const [sortOrder, setSortOrder] = useState('asc'); // Default ascending
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pendingTransfers, setPendingTransfers] = useState({}); // Map of assetId -> transfer info
  const [visibleColumns, setVisibleColumns] = useState({
    image: true,
    assetTag: true,
    description: true,
    brand: true,
    purchaseDate: true,
    cost: true,
    status: true,
    condition: true,
    purchasedFrom: true,
    model: true,
    serialNumber: true,
    site: true,
    location: true,
    category: true,
    department: true,
    assignedTo: true,
    createdAt: true,
    action: true,
  });

  const [filters, setFilters] = useState({
    assetTag: '',
    description: '',
    brand: '',
    model: '',
    serialNumber: '',
    status: '',
    condition: '',
    purchasedFrom: '',
    purchaseDateFrom: '',
    purchaseDateTo: '',
    costFrom: '',
    costTo: '',
    site: '',
    location: '',
    category: '',
    department: '',
    assignedTo: '',
    assignedDateFrom: '',
    assignedDateTo: '',
    expectedReturnDateFrom: '',
    expectedReturnDateTo: '',
    createdAtFrom: '',
    createdAtTo: '',
  });

  useEffect(() => {
    fetchFilterData();
  }, []);

  // Fetch assets when filters, search, pagination, or sorting changes (after permissions load)
  useEffect(() => {
    if (!permissionsReady || !canViewAssetsList) return;
    fetchAssets();
  }, [
    currentPage,
    itemsPerPage,
    searchTerm,
    filters,
    sortBy,
    sortOrder,
    permissionsReady,
    canViewAssetsList,
  ]);

  const fetchAssets = async () => {
    try {
      // DON'T set loading during search - prevents focus loss
      // setLoading(true);

      // Build query parameters
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      // Add search term
      if (searchTerm) {
        params.search = searchTerm;
      }

      // Add all filter parameters
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params[key] = filters[key];
        }
      });

      // Add sorting parameters
      params.sortBy = sortBy;
      params.sortOrder = sortOrder;

      const response = await api.getAssets(params);
      if (response.success) {
        // Save focused element
        const activeElement = document.activeElement;
        const wasSearchFocused = activeElement === searchInputRef.current;
        
        setAssets(response.data || []);
        setTotalAssets(response.total || 0);
        setTotalPages(response.totalPages || 1);
        
        // Fetch pending transfers for assets with in_transfer status
        fetchPendingTransfers(response.data || []);
        
        // Restore focus if needed
        if (wasSearchFocused && searchInputRef.current) {
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Error fetching assets');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortBy(field);
      setSortOrder('asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  const fetchFilterData = async () => {
    try {
      // Fetch sites
      const sitesRes = await api.getSites({ isActive: 'true' });
      if (sitesRes.success) {
        setSites(sitesRes.data || []);
      }

      // Fetch departments
      const deptsRes = await api.getDepartments({ isActive: 'true' });
      if (deptsRes.success) {
        setDepartments(deptsRes.data || []);
      }

      // All active users (employees + admins) for Assigned To filter
      const usersRes = await api.getUsers({ isActive: 'true', limit: 10000 });
      if (usersRes.success) {
        setEmployees(usersRes.data || []);
      }

      // Fetch categories
      const categoriesRes = await api.getCategories({ isActive: 'true' });
      if (categoriesRes.success) {
        setCategories(categoriesRes.data || []);
      }

      // Fetch locations
      const locationsRes = await api.getLocations({ isActive: 'true' });
      if (locationsRes.success) {
        setLocations(locationsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchPendingTransfers = async (assetsList) => {
    try {
      // Get assets with in_transfer status
      const inTransferAssets = assetsList.filter(asset => asset.status === 'in_transfer');
      if (inTransferAssets.length === 0) {
        setPendingTransfers({});
        return;
      }

      // Fetch pending handovers (we'll filter for transfers on frontend)
      const handoversResponse = await api.getHandovers({ 
        acknowledgementStatus: 'pending'
      });
      
      if (handoversResponse.success && handoversResponse.data) {
        const transfers = {};
        // Filter for transfer type handovers and get the most recent one per asset
        const transferHandovers = handoversResponse.data
          .filter(handover => handover.handoverType === 'transfer' && handover.acknowledgementStatus === 'pending')
          .sort((a, b) => new Date(b.createdAt || b.assignedDate) - new Date(a.createdAt || a.assignedDate));
        
        transferHandovers.forEach(handover => {
          const assetId = handover.asset?._id || handover.asset?.id || handover.asset;
          if (assetId) {
            const normalizedAssetId = assetId.toString();
            // Only set if not already set (to get the most recent transfer)
            if (!transfers[normalizedAssetId]) {
              // assignedTo is the RECIPIENT (person receiving the transfer)
              // assignedBy is the SENDER (person who initiated the transfer)
              // Make sure we get the name correctly - handle both object and populated formats
              const senderName = handover.assignedBy?.name || 
                                (typeof handover.assignedBy === 'string' ? handover.assignedBy : 'Unknown');
              const recipientName = handover.assignedTo?.name || 
                                   (typeof handover.assignedTo === 'string' ? handover.assignedTo : 'Unknown');
              
              transfers[normalizedAssetId] = {
                to: recipientName, // Recipient name
                toId: handover.assignedTo?._id || handover.assignedTo?.id,
                from: senderName // Sender name (person who transferred)
              };
            }
          }
        });
        setPendingTransfers(transfers);
      }
    } catch (error) {
      console.error('Error fetching pending transfers:', error);
    }
  };

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  // Handle search with manual debouncing - NO state updates while typing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    
    searchTimerRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 500);
  };

  const handleFilterChange = (name, value) => {
    // For non-debounced fields, update filter directly
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      assetTag: '',
      description: '',
      brand: '',
      model: '',
      serialNumber: '',
      status: '',
      condition: '',
      purchasedFrom: '',
      purchaseDateFrom: '',
      purchaseDateTo: '',
      costFrom: '',
      costTo: '',
      site: '',
      location: '',
      category: '',
      department: '',
      assignedTo: '',
      assignedDateFrom: '',
      assignedDateTo: '',
      expectedReturnDateFrom: '',
      expectedReturnDateTo: '',
      createdAtFrom: '',
      createdAtTo: '',
    });
    setSearchTerm('');
    // Clear the search input manually
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  // Fetch all filtered assets for export (without pagination)
  const fetchAllFilteredAssets = async () => {
    const params = {
      limit: 10000, // Large limit to get all filtered results
    };

    if (searchTerm) {
      params.search = searchTerm;
    }

    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params[key] = filters[key];
      }
    });

    const response = await api.getAssets(params);
    return response.success ? response.data : [];
  };

  const handleExportExcel = async () => {
    try {
      if (totalAssets === 0) {
        toast.warning('No assets to export');
        return;
      }

      const allAssets = await fetchAllFilteredAssets();
      if (allAssets.length === 0) {
        toast.warning('No assets to export');
        return;
      }

      exportToExcel(allAssets, visibleColumns, columnLabels, 'assets_export');
      toast.success(`Exported ${allAssets.length} assets to Excel`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      if (totalAssets === 0) {
        toast.warning('No assets to export');
        return;
      }

      const allAssets = await fetchAllFilteredAssets();
      if (allAssets.length === 0) {
        toast.warning('No assets to export');
        return;
      }

      exportToPDF(allAssets, visibleColumns, columnLabels, 'assets_export');
      toast.success(`Exported ${allAssets.length} assets to PDF`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAssets.length === 0) return;

    try {
      setProcessing(true);
      
      // Use bulk delete endpoint to soft delete all selected assets together
      const response = await api.bulkDeleteAssets(selectedAssets);
      
      if (response.success) {
        const count = response.count || selectedAssets.length;
        toast.success(`${count} asset${count !== 1 ? 's' : ''} moved to trash`);
        
        if (response.skipped > 0) {
          toast.warning(`${response.skipped} asset${response.skipped !== 1 ? 's' : ''} were already deleted or not found`);
        }
        
        setShowBulkDeleteModal(false);
        setSelectedAssets([]);
        fetchAssets(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to delete assets');
      }
    } catch (error) {
      toast.error('Error deleting assets: ' + (error.message || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkUpdateCondition = async () => {
    if (selectedAssets.length === 0 || !selectedCondition) return;

    try {
      setProcessing(true);
      
      // Use bulk update endpoint to update all selected assets together
      const response = await api.bulkUpdateCondition(selectedAssets, selectedCondition);
      
      if (response.success) {
        const count = response.count || selectedAssets.length;
        const conditionLabel = selectedCondition.replace('_', ' ');
        toast.success(`${count} asset${count !== 1 ? 's' : ''} condition updated to ${conditionLabel}`);
        
        setShowUpdateConditionModal(false);
        setSelectedAssets([]);
        setSelectedCondition('');
        fetchAssets(); // Refresh the list to show updated conditions
      } else {
        toast.error(response.message || 'Failed to update asset conditions');
      }
    } catch (error) {
      toast.error('Error updating asset condition: ' + (error.message || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  const handlePrint = async () => {
    if (totalAssets === 0) {
      toast.warning('No assets to print');
      return;
    }

    // Fetch all filtered assets for printing
    const allAssets = await fetchAllFilteredAssets();
    if (allAssets.length === 0) {
      toast.warning('No assets to print');
      return;
    }

    // Create a print-friendly window
    const printWindow = window.open('', '_blank');
    const { headers, data, count } = preparePrintData(allAssets, visibleColumns, columnLabels);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Asset Management Report</title>
          <style>
            @media print {
              @page {
                margin: 1cm;
                size: landscape;
              }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              font-size: 12px;
            }
            h1 {
              color: #2d3748;
              margin-bottom: 5px;
            }
            .info {
              color: #718096;
              margin-bottom: 20px;
              font-size: 11px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            th {
              background-color: #667eea;
              color: white;
              padding: 10px;
              text-align: left;
              font-weight: 600;
              border: 1px solid #ddd;
            }
            td {
              padding: 8px;
              border: 1px solid #ddd;
            }
            tr:nth-child(even) {
              background-color: #f7fafc;
            }
            tr:hover {
              background-color: #edf2f7;
            }
          </style>
        </head>
        <body>
          <h1>Asset Management Report</h1>
          <div class="info">
            Generated on: ${new Date().toLocaleString()}<br>
            Total Assets: ${count}
          </div>
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data.map(row => `
                <tr>
                  ${row.map(cell => `<td>${cell}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);

    printWindow.document.close();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Column labels for export and print
  const columnLabels = {
    image: 'Image',
    assetTag: 'Asset Tag ID',
    description: 'Description',
    brand: 'Brand',
    purchaseDate: 'Purchase Date',
    cost: 'Cost',
    status: 'Status',
    condition: 'Condition',
    purchasedFrom: 'Purchased From',
    model: 'Model',
    serialNumber: 'Serial Number',
    site: 'Site',
    location: 'Location',
    category: 'Category',
    department: 'Department',
    assignedTo: 'Assigned To',
    createdAt: 'Date Created',
    action: 'Action'
  };

  // Load saved column preferences on mount
  useEffect(() => {
    const savedColumns = localStorage.getItem('assetTableColumns');
    if (savedColumns) {
      try {
        const parsed = JSON.parse(savedColumns);
        setVisibleColumns(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Error loading saved column preferences:', error);
      }
    }
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  const canBulkCheckout = canUseFeature([PERMISSIONS.CHECKOUT]);
  const canBulkCheckin = canUseFeature([PERMISSIONS.CHECKIN]);
  const canBulkUpdateCondition = canUseFeature([PERMISSIONS.EDIT_ASSETS]);
  const canBulkDeleteAssets = canUseFeature([PERMISSIONS.DELETE_ASSETS]);
  const hasAnyBulkAssetAction =
    canBulkCheckout || canBulkCheckin || canBulkUpdateCondition || canBulkDeleteAssets;
  const enableBulkSelection = hasAnyBulkAssetAction && canViewAssetsList;

  const rowActionFlags = useMemo(
    () => ({
      view: canUseFeature([PERMISSIONS.VIEW_ALL_ASSETS, PERMISSIONS.VIEW_ASSIGNED_ASSETS]),
      edit: canUseFeature([PERMISSIONS.EDIT_ASSETS]),
      checkout: canUseFeature([PERMISSIONS.CHECKOUT]),
      checkin: canUseFeature([PERMISSIONS.CHECKIN]),
      remind: canUseFeature([PERMISSIONS.CHECKOUT]),
      delete: canUseFeature([PERMISSIONS.DELETE_ASSETS]),
    }),
    [canUseFeature]
  );

  const canAccessAssetsModuleAddOnly = useMemo(
    () => !canViewAssetsList && canUseFeature([PERMISSIONS.ADD_ASSETS]),
    [canViewAssetsList, canUseFeature]
  );

  if (!permissionsReady) {
    return <Loader />;
  }

  if (!canViewAssetsList) {
    if (canAccessAssetsModuleAddOnly) {
      return (
        <div className="asset-list-page" style={{ padding: '16px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#2d3748' }}>Assets</h1>
            <button
              type="button"
              onClick={() => navigate(addAssetPath)}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>+</span>
              <span>Add Asset</span>
            </button>
          </div>
          <div
            style={{
              background: 'white',
              padding: '24px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0',
            }}
          >
            <p style={{ margin: 0, color: '#4a5568', fontSize: '14px', lineHeight: 1.65 }}>
              You can register new assets using <strong>Add Asset</strong>. Your role does not include
              browsing the full inventory—to search or view lists, an administrator can grant{' '}
              <strong>View all assets</strong> or <strong>View assigned assets</strong> under Settings → User Roles.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div style={{ padding: '24px', maxWidth: '560px', margin: '0 auto' }}>
        <div
          style={{
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            borderRadius: '12px',
            padding: '20px',
            color: '#9a3412',
            fontSize: '14px',
            lineHeight: 1.5,
          }}
        >
          You do not have permission to view assets. Ask an administrator to enable{' '}
          <strong>View all assets</strong> or <strong>View assigned assets</strong> for your role in
          Settings → User Roles.
        </div>
      </div>
    );
  }

  if (loading && assets.length === 0) {
    return <Loader />;
  }

  return (
    <div className="asset-list-page" style={{ padding: '16px' }}>
      {/* Actions */}
      {canUseFeature([PERMISSIONS.ADD_ASSETS]) && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '20px'
        }}>
          <button
            type="button"
            onClick={() => navigate(addAssetPath)}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>+</span>
            <span>Add Asset</span>
          </button>
        </div>
      )}

      {/* Search and Export Bar */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search Bar */}
          <div style={{ flex: 1, minWidth: '250px' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search Assets..."
              defaultValue={searchTerm}
              onChange={handleSearchChange}
              autoComplete="off"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>
          {/* <button
            onClick={() => {
              setCurrentPage(1);
              fetchAssets();
            }}
            style={{
              padding: '10px 20px',
              background: '#38a169',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px'
            }}
          >
            Search
          </button> */}
          {selectedAssets.length > 0 && hasAnyBulkAssetAction && canViewAssetsList && (() => {
                // Get selected asset objects
                const selectedAssetObjects = assets.filter(a => selectedAssets.includes(a.id || a._id));
                
                // Check if all selected assets are available (for checkout)
                const allAvailable = selectedAssetObjects.length > 0 && 
                  selectedAssetObjects.every(asset => asset.status === 'available');
                
                // Check if all selected assets are received or issue_reported (for checkin)
                const allCheckinEligible = selectedAssetObjects.length > 0 && 
                  selectedAssetObjects.every(asset => 
                    asset.status === 'received' || asset.status === 'issue_reported'
                  );
                
                return (
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowActionDropdown(!showActionDropdown)}
                      onBlur={() => setTimeout(() => setShowActionDropdown(false), 200)}
                      style={{
                        padding: '10px 20px',
                        background: showActionDropdown ? '#5568d3' : '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'background 0.2s ease',
                        boxShadow: showActionDropdown ? '0 2px 4px rgba(102, 126, 234, 0.3)' : 'none'
                      }}
                    >
                      <span>⚙️</span>
                      <span>Actions ({selectedAssets.length})</span>
                      <span style={{ fontSize: '10px', marginLeft: '4px' }}>{showActionDropdown ? '▲' : '▼'}</span>
                    </button>
                    {showActionDropdown && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '6px',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                          zIndex: 1000,
                          minWidth: '220px',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={() => setShowActionDropdown(true)}
                        onMouseLeave={() => setShowActionDropdown(false)}
                      >
                        <div style={{ padding: '4px 0' }}>
                          {canBulkCheckout && (
                          <button
                            onClick={() => {
                              if (allAvailable) {
                                navigate('/admin/checkout/form', { state: { selectedAssets: selectedAssetObjects } });
                                setShowActionDropdown(false);
                              }
                            }}
                            disabled={!allAvailable}
                            title={!allAvailable ? 'All selected assets must have "available" status' : ''}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              background: 'white',
                              color: allAvailable ? '#4a5568' : '#a0aec0',
                              border: 'none',
                              textAlign: 'left',
                              cursor: allAvailable ? 'pointer' : 'not-allowed',
                              fontSize: '13px',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              transition: 'background 0.15s ease',
                              opacity: allAvailable ? 1 : 0.6
                            }}
                            onMouseEnter={(e) => {
                              if (allAvailable) {
                                e.target.style.background = '#f0f4ff';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'white';
                            }}
                          >
                            <span style={{ fontSize: '16px' }}>📤</span>
                            <span>Check-out</span>
                          </button>
                          )}
                          {canBulkCheckin && (
                          <button
                            onClick={() => {
                              if (allCheckinEligible) {
                                navigate('/admin/checkin/form', { state: { selectedAssets: selectedAssetObjects } });
                                setShowActionDropdown(false);
                              }
                            }}
                            disabled={!allCheckinEligible}
                            title={!allCheckinEligible ? 'All selected assets must have "received" or "issue_reported" status' : ''}
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              background: 'white',
                              color: allCheckinEligible ? '#4a5568' : '#a0aec0',
                              border: 'none',
                              textAlign: 'left',
                              cursor: allCheckinEligible ? 'pointer' : 'not-allowed',
                              fontSize: '13px',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              transition: 'background 0.15s ease',
                              opacity: allCheckinEligible ? 1 : 0.6
                            }}
                            onMouseEnter={(e) => {
                              if (allCheckinEligible) {
                                e.target.style.background = '#f0f4ff';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'white';
                            }}
                          >
                            <span style={{ fontSize: '16px' }}>📥</span>
                            <span>Check-In</span>
                          </button>
                          )}
                        {canBulkUpdateCondition && (
                        <button
                          onClick={() => {
                            setSelectedCondition(''); // Reset to empty when opening modal
                            setShowUpdateConditionModal(true);
                            setShowActionDropdown(false);
                          }}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: 'white',
                            color: '#4a5568',
                            border: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#f0f4ff'}
                          onMouseLeave={(e) => e.target.style.background = 'white'}
                        >
                          <span style={{ fontSize: '16px' }}>🔧</span>
                          <span>Update Asset Condition</span>
                        </button>
                        )}
                        {canBulkDeleteAssets && (canBulkCheckout || canBulkCheckin || canBulkUpdateCondition) && (
                        <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
                        )}
                        {canBulkDeleteAssets && (
                        <button
                          onClick={() => {
                            setShowBulkDeleteModal(true);
                            setShowActionDropdown(false);
                          }}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: 'white',
                            color: '#e53e3e',
                            border: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#fed7d7'}
                          onMouseLeave={(e) => e.target.style.background = 'white'}
                        >
                          <span style={{ fontSize: '16px' }}>🗑️</span>
                          <span>Delete Assets</span>
                        </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}
          {canViewAllAssets && (
            <>
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  padding: '10px 20px',
                  background: '#f7fafc',
                  color: '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px'
                }}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
              <button
                onClick={() => navigate('/admin/settings/columns')}
                style={{
                  padding: '10px 20px',
                  background: '#f7fafc',
                  color: '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px'
                }}
              >
                Setup Columns
              </button>
            </>
          )}
          {canUseFeature([PERMISSIONS.VIEW_ASSET_REPORTS]) && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleExportExcel}
                style={{
                  padding: '10px 16px',
                  background: '#f7fafc',
                  color: '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '12px'
                }}
                title="Export to Excel"
              >
                📊 Excel
              </button>
              <button
                onClick={handleExportPDF}
                style={{
                  padding: '10px 16px',
                  background: '#f7fafc',
                  color: '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '12px'
                }}
                title="Export to PDF"
              >
                📄 PDF
              </button>
              <button
                onClick={handlePrint}
                style={{
                  padding: '10px 16px',
                  background: '#f7fafc',
                  color: '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '12px'
                }}
                title="Print Table"
              >
                🖨️ Print
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {canViewAllAssets && showFilters && (
        <div style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '16px'
        }}>
          <AssetFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            sites={sites}
            departments={departments}
            categories={categories}
            locations={locations}
            employees={employees}
          />
        </div>
      )}

      {/* Table */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <AssetTable
          assets={assets}
          visibleColumns={visibleColumns}
          pendingTransfers={pendingTransfers}
          selectedAssets={selectedAssets}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          bulkSelectionEnabled={enableBulkSelection}
          rowActionFlags={rowActionFlags}
          onSelectAsset={
            enableBulkSelection
              ? (id, isSelected) => {
                  if (isSelected) {
                    setSelectedAssets([...selectedAssets, id]);
                  } else {
                    setSelectedAssets(selectedAssets.filter((assetId) => assetId !== id));
                  }
                }
              : undefined
          }
          onSelectAll={enableBulkSelection ? (ids) => setSelectedAssets(ids) : undefined}
          onView={rowActionFlags.view ? (id) => navigate(`${assetsBasePath}/${id}`) : undefined}
          onEdit={rowActionFlags.edit ? (id) => navigate(`${assetsBasePath}/${id}/edit`) : undefined}
          onDelete={
            rowActionFlags.delete
              ? (id) => {
                  setAssetToDelete(id);
                  setShowDeleteModal(true);
                }
              : undefined
          }
          onCheckout={
            rowActionFlags.checkout
              ? (asset) => {
                  navigate('/admin/checkout/form', { state: { selectedAssets: [asset] } });
                }
              : undefined
          }
          onCheckin={
            rowActionFlags.checkin
              ? (asset) => {
                  navigate('/admin/checkin/form', { state: { selectedAssets: [asset] } });
                }
              : undefined
          }
          onRemind={
            rowActionFlags.remind
              ? async (asset) => {
            try {
              const assetId = asset.id || asset._id;
              
              // Fetch pending handovers for this asset
              const response = await api.getHandovers({ 
                acknowledgementStatus: 'pending' 
              });
              
              if (response.success) {
                const handovers = response.data || [];
                // Find handover for this asset
                // For in_transfer status, look for transfer type handover
                // For pending status, look for checkout type handover
                const handover = handovers.find(h => {
                  const handoverAssetId = h.asset?._id || h.asset?.id || h.asset;
                  const matchesAsset = handoverAssetId?.toString() === assetId?.toString();
                  
                  if (asset.status === 'in_transfer') {
                    // For transfers, find transfer type handover
                    return matchesAsset && h.handoverType === 'transfer';
                  } else {
                    // For regular pending, find checkout type (or no handoverType, which defaults to checkout)
                    return matchesAsset && (!h.handoverType || h.handoverType === 'checkout');
                  }
                });
                
                if (handover) {
                  const handoverId = handover.id || handover._id;
                  await api.sendHandoverReminder(handoverId);
                  
                  // For transfers, assignedTo is the recipient
                  // For checkouts, assignedTo is also the recipient
                  const recipientEmail = handover.assignedTo?.email || 'employee';
                  const recipientName = handover.assignedTo?.name || 'employee';
                  toast.success(`Reminder email sent to ${recipientName} (${recipientEmail})`);
                } else {
                  toast.error('No pending handover found for this asset');
                }
              } else {
                toast.error('Failed to fetch handover information');
              }
            } catch (error) {
              console.error('Error sending reminder email:', error);
              toast.error('Failed to send reminder email');
            }
          }
              : undefined
          }
        />
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalAssets}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(newItemsPerPage) => {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmActionModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setAssetToDelete(null);
        }}
        onConfirm={async () => {
          if (assetToDelete) {
            try {
              setProcessing(true);
              const response = await api.deleteAsset(assetToDelete);
              if (response.success) {
                toast.success('Asset moved to trash successfully');
                setShowDeleteModal(false);
                setAssetToDelete(null);
                setSelectedAssets([]);
              fetchAssets();
              } else {
                toast.error(response.message || 'Error deleting asset');
              }
            } catch (error) {
              console.error('Error deleting asset:', error);
              toast.error('Error deleting asset: ' + (error.message || 'Unknown error'));
            } finally {
              setProcessing(false);
            }
          }
        }}
        title="Move Asset to Trash"
        message="Are you sure you want to move this asset to trash? You can restore it later from the Trash page."
        confirmText="Move to Trash"
        cancelText="Cancel"
        variant="danger"
        loading={processing}
      />

      {/* Bulk Delete Modal */}
      <ConfirmActionModal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title="Move Selected Assets to Trash"
        message={`Are you sure you want to move ${selectedAssets.length} asset${selectedAssets.length !== 1 ? 's' : ''} to trash? You can restore them later from the Trash page.`}
        confirmText="Move to Trash"
        cancelText="Cancel"
        variant="danger"
        loading={processing}
      />

      {/* Update Condition Modal */}
      {showUpdateConditionModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowUpdateConditionModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              minWidth: '400px',
              maxWidth: '500px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#2d3748' }}>
              Update Asset Condition
            </h2>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#718096' }}>
              Update condition for <strong>{selectedAssets.length}</strong> selected asset{selectedAssets.length !== 1 ? 's' : ''} to the same condition
            </p>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#4a5568' }}>
                Condition
              </label>
              <select
                value={selectedCondition}
                onChange={(e) => setSelectedCondition(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select Condition</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
                <option value="damaged">Damaged</option>
                <option value="repair_needed">Repair Needed</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowUpdateConditionModal(false)}
                disabled={processing}
                style={{
                  padding: '10px 20px',
                  background: '#f7fafc',
                  color: '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: processing ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  opacity: processing ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkUpdateCondition}
                disabled={processing || !selectedCondition}
                style={{
                  padding: '10px 20px',
                  background: selectedCondition ? '#667eea' : '#cbd5e0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (processing || !selectedCondition) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  opacity: (processing || !selectedCondition) ? 0.6 : 1
                }}
              >
                {processing ? 'Updating...' : 'Update Condition'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AssetListPage;
